/**
 * Privacy & Consent Manager
 * GDPR / CCPA compliant first-visit consent gate.
 *
 * Flow:
 *   - On first visit: show consent modal
 *   - ACCEPT → store consent, call onAccepted callback (initializes AudioContext + game)
 *   - DECLINE → show exit screen with redirect option
 *   - "Delete All Data" → wipes all localStorage + IndexedDB, resets consent flag
 */

const CONSENT_KEY = 'waves_yo_consent_v1';
const CONSENT_ACCEPTED = 'accepted';

export type ConsentCallback = () => void;

/** Returns true if the user has already accepted consent in this browser. */
export function hasConsented(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === CONSENT_ACCEPTED;
  } catch {
    // localStorage unavailable (private mode, storage full, etc.) — treat as no consent
    return false;
  }
}

/** Stores timestamped consent acceptance in localStorage. */
function storeConsent(): void {
  try {
    localStorage.setItem(CONSENT_KEY, CONSENT_ACCEPTED);
    localStorage.setItem('waves_yo_consent_ts', new Date().toISOString());
  } catch {
    // Non-fatal — game proceeds without persistence
    console.warn('[Privacy] Could not store consent — localStorage unavailable.');
  }
}

/**
 * Deletes all Waves-Yo data from localStorage and IndexedDB.
 * Called from the Privacy & Data section and the delete button.
 */
export async function deleteAllData(): Promise<void> {
  // Clear localStorage
  try {
    const keysToDelete = Object.keys(localStorage).filter((k) =>
      k.startsWith('waves_yo')
    );
    keysToDelete.forEach((k) => localStorage.removeItem(k));
  } catch {
    console.warn('[Privacy] Could not clear localStorage.');
  }

  // Clear IndexedDB databases used by the game
  try {
    const dbs = await indexedDB.databases?.();
    if (dbs) {
      for (const db of dbs) {
        if (db.name?.startsWith('waves_yo')) {
          indexedDB.deleteDatabase(db.name);
        }
      }
    }
  } catch {
    console.warn('[Privacy] Could not enumerate IndexedDB databases.');
  }

  // Reload so the consent modal appears fresh
  window.location.reload();
}

/**
 * Initializes the consent UI.
 * If consent was already given, immediately calls onAccepted.
 * Otherwise shows the modal and wires up all button handlers.
 */
export function initConsent(onAccepted: ConsentCallback): void {
  if (hasConsented()) {
    // Returning user — skip modal and launch game
    onAccepted();
    hideSplash();
    return;
  }

  // First visit — show modal
  const overlay = document.getElementById('consent-overlay');
  const btnAccept = document.getElementById('btn-accept');
  const btnDecline = document.getElementById('btn-decline');
  const declineScreen = document.getElementById('decline-screen');
  const btnExitSite = document.getElementById('btn-exit-site');
  const btnGoBack = document.getElementById('btn-go-back');
  const deleteBtn = document.getElementById('delete-all-data-btn');

  if (!overlay || !btnAccept || !btnDecline || !declineScreen) {
    // Fallback: if DOM is broken, accept silently
    onAccepted();
    hideSplash();
    return;
  }

  // Show modal (it's visible by default for first visit)
  overlay.removeAttribute('hidden');

  // ── ACCEPT ──────────────────────────────────────────────────────────────
  btnAccept.addEventListener('click', () => {
    storeConsent();
    overlay.classList.add('hidden');
    hideSplash();
    onAccepted();
  });

  // ── DECLINE ─────────────────────────────────────────────────────────────
  btnDecline.addEventListener('click', () => {
    overlay.classList.add('hidden');
    declineScreen.classList.remove('hidden');
  });

  // ── EXIT SITE ───────────────────────────────────────────────────────────
  btnExitSite?.addEventListener('click', () => {
    window.location.href = 'https://www.google.com';
  });

  // ── GO BACK (change mind) ────────────────────────────────────────────────
  btnGoBack?.addEventListener('click', () => {
    declineScreen.classList.add('hidden');
    overlay.classList.remove('hidden');
  });

  // ── DELETE ALL DATA ──────────────────────────────────────────────────────
  deleteBtn?.addEventListener('click', () => {
    if (confirm('This will permanently delete all your saved game data. Are you sure?')) {
      void deleteAllData();
    }
  });
}

/** Fades out and removes the splash screen. */
export function hideSplash(): void {
  const splash = document.getElementById('splash');
  if (splash) {
    splash.classList.add('hidden');
    splash.addEventListener('transitionend', () => splash.remove(), { once: true });
  }
}
